import getUserConfiguration from 'owa-session-store/lib/actions/getUserConfiguration';
import isConsumer from 'owa-session-store/lib/utils/isConsumer';
import { format } from 'owa-strings/utilities/format';
import { getAccessTokenforResourceAsLazy, getDelegationTokenForOwa } from 'owa-tokenprovider';
import { getGuid } from 'owa-guid';
import { isSuccessStatusCode } from 'owa-http-status-codes';
import { logUsage } from 'owa-analytics';
import { trace } from 'owa-trace';
const ARC_END_POINT =
    'https://arc.msn.com/v4/api/selection?placement={0}&country={1}&pl={2}&bcnt=5&nct=1&asid={3}';
const locale = 'EN-US';
const requestHeaderAcceptLanguage = 'en-US'; // This was conveyed by IRIS team to make the necessary change and send only this value
const TOKEN_SCOPE = 'service::ads.arcct.msn.com::MBI_SSL';
const TOKEN_RESOURCE = 'https://arc.msn.com/v4';
const TOKEN_API_NAME = 'OwaIrisStore';
const IRIS_CALL_FAILED = 'IrisCallFailed';

export const makeArcCall = async (placementId: string, realtimeSignals?: string): Promise<any> => {
    const headers = new Headers();
    const consumer: boolean = isConsumer();
    let token;
    if (consumer) {
        token = await getDelegationTokenForOwa(TOKEN_SCOPE);
    } else {
        const [authToken, tokenPromise] = getAccessTokenforResourceAsLazy(
            TOKEN_RESOURCE,
            TOKEN_API_NAME
        );
        if (!authToken) {
            token = await tokenPromise;
        } else {
            token = authToken;
        }
    }

    // Split the locale with a - and get the last two letter which is mostly the country code
    let country = '';
    if (locale) {
        country = locale.split('-').pop();
    }

    let arcUri: string = realtimeSignals
        ? ARC_END_POINT.concat('&' + realtimeSignals)
        : ARC_END_POINT;

    /**
     * Add authorization token only when there is a valid token present or add puid if not valid
     */
    if (token) {
        headers.append('authorization', consumer ? `WLID1.1 t=${token}` : `Bearer ${token}`);
    } else {
        const puid = getUserConfiguration().SessionSettings?.UserPuid;
        arcUri = arcUri.concat('&puid=' + puid);
    }
    headers.set('accept-language', requestHeaderAcceptLanguage);
    const request: RequestInit = { method: 'GET', headers: headers };

    return fetch(format(arcUri, placementId, country, locale, getGuid()), request).then(
        handleResponse
    );
};

export const makeBeaconCall = async (uri: string, beaconName: string, campaignId: string) => {
    const request: RequestInit = { method: 'GET' };

    // Replace the content between {ACTION} with beacon name.
    const formattedUri = uri.replace(new RegExp('{' + 'ACTION' + '}'), beaconName);

    fetch(formattedUri, request)
        .then(response => handleResponse(response, { campaignId, apiCall: `beacon-${beaconName}` }))
        .catch(error => handleFailure(error, campaignId, `beacon-${beaconName}`));
};

export const makeImpressionCall = async (uri: string, campaignId: string) => {
    const request: RequestInit = { method: 'GET' };
    fetch(uri, request)
        .then(response => handleResponse(response, { campaignId, apiCall: 'impression' }))
        .catch(error => handleFailure(error, campaignId, 'impression'));
};

const handleResponse = (response: Response, data?: any) => {
    try {
        const { status, statusText } = response;
        let isCallSuccess = false;
        if (!isSuccessStatusCode(status)) {
            data &&
                logUsage(
                    IRIS_CALL_FAILED,
                    { ...data, errorMessage: `${status} ${statusText}` },
                    { isCore: true }
                );
            trace.warn(`Failed to make successful IRIS API call. ${status} ${statusText}`);
            return { value: null, isCallSuccess };
        }

        data && logUsage('IrisCallSuccess', data, { isCore: true });

        isCallSuccess = true;
        const value = response.json();
        return { value, isCallSuccess };
    } catch (error) {
        trace.warn(`Error while handling IRIS response. ${error.message}`);

        error.message = `[handleResponse] Failed to handle response. ${error.message}`;
        throw new Error(error);
    }
};

const handleFailure = (error, campaignId: string, action: string) => {
    logUsage(
        IRIS_CALL_FAILED,
        { campaignId, apiCall: action, errorMessage: error.message },
        { isCore: true }
    );
    return { value: null, isCallSuccess: false };
};
